import useLocales from './useLocales';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useClientIdRedirect, buildIdentitySettings } from '@gsgroup/gsgroup-react-ui';
import { getLoginFlowAsync } from '../Common/api-clients/userInformationClient';
import { setClientOptions } from '../Common/Utils/backendDataHelper';
import { BackendData } from '../utils/backendDataParser';
import { getAppSettings } from '../Common/Utils/localStorageHelper';
import { getUserAsync, login, logout } from '../Common/api-clients/authClient';

export function useOidcClientLogin() {
  const navigate = useNavigate();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const appSettings = getAppSettings();

  async function validateClientId(clientId: string) {
    const policyName = await getLoginFlowAsync(clientId);

    const user = await getUserAsync();

    if (user.isAuthenticated) {
      const isAdminSession = sessionStorage.getItem('isAdmin') === 'true';
      if (!isAdminSession) {
        // User is authenticated but not via the admin flow — they came in via `/` with the
        // default policy. Clear stale client state, raise the `adminFlowRequired` flag (so
        // App.tsx routes them to /admin after the round-trip instead of letting AuthGuard
        // push them back into the default login flow) and sign them out.
        localStorage.removeItem('clientOptions');
        localStorage.removeItem('selectedClient');
        localStorage.setItem('adminFlowRequired', 'true');
        logout();
        return;
      }

      enqueueSnackbar(translate('ClientSelector.AlreadyLoggedIn'), { variant: 'info' });
      navigate("/");
      return;
    }

    const clientOption: BackendData = { backendName: 0, name: clientId, tenantId: null };
    setClientOptions([clientOption]);
    //Per session indication that user came from admin login process.
    sessionStorage.setItem('isAdmin', 'true');

    login(policyName, appSettings.companyGuid);
  }

  const { clientId: existingClientId, handleSubmit } = useClientIdRedirect({
    validateClientId,
    isInvalidClientErr: err => {
      const invalid = err instanceof Error && err.message === 'INVALID_CLIENT';
      if (invalid) {
        enqueueSnackbar(translate('ClientSelector.InvalidClientId'), { variant: 'error' });
      }
      return invalid;
    },
    resetPath: '/admin'
  });

  return { existingClientId, handleSubmit };
}